import {createAsyncThunk} from "@reduxjs/toolkit";
import {HOSTNAME} from "../../configs/Domain";
import {getAuthHeader, prepareJsonPayload} from "../../store/utils";
import {appendStreamResponse, appendUserMessage} from "../../store/slices/gpt.slice";


export const fetchGptResponse = createAsyncThunk(
    'inference/fetchGptResponse',
    /**
     *
     * @param promptData {string} - The prompt to send to server to passthrough to GPT service
     * @param id {string} - The UUID of the prompt generated in useGptResponses hook
     * @param srcLocation {string, optional} - Where within the platform the prompt was generated
     * @param promptTemplate {string, optional} - The template used to generate the prompt
     * @param rejectWithValue
     */
    async ({ promptData, id, srcLocation, promptHeadKey }, { rejectWithValue }) => {
        try {
            const requestData = { prompt: promptData };
            if (promptHeadKey) {
                requestData.prompt_head = promptHeadKey;
            }
            const response = await fetch(`${HOSTNAME}/inference/general`,
                prepareJsonPayload({
                    data: requestData,
                    method: "POST"
                }));

            if (!response.ok) {
                throw new Error('Network response was not ok');
            }
            const data = await response.json();
            return { id, data, timestamp: new Date().toISOString(), srcLocation };  // Include UUID in the response
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);


// inferenceApi.js (or wherever your thunk is defined)
export const fetchGptResponseStream = createAsyncThunk(
  'inference/fetchGptResponseStream',
  async ({ promptData, id, groupId = null }, { rejectWithValue, dispatch }) => {
    const requestData = { prompt: promptData };
    if (groupId) {
      requestData.group_id = groupId; // or handle on backend as needed
    }

    try {
      const response = await fetch(`${HOSTNAME}/inference/stream`, prepareJsonPayload({
        data: requestData,
        method: "POST",
      }));
      if (!response.ok) throw new Error("Network response was not ok");

      // Optionally dispatch user message to store
      dispatch(appendUserMessage({
        id,
        groupId,
        userChunk: promptData
      }));

      // Stream the assistant's response in chunks
      const reader = response.body.getReader();
      const decoder = new TextDecoder("utf-8");

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        dispatch(appendStreamResponse({ id, groupId, chunk }));
      }

      // Done streaming
      return { id, timestamp: new Date().toISOString() };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);


export const streamChatResponse = createAsyncThunk(
  'inference/streamChatResponse',
  async ({
    id,
    groupId = null,
    user_prompt,  // The actual user input/question
    portfolio_id, // Required for portfolio analysis
    lookbacks = ["3_mo", "6_mo"], // Optional with default
    chatContext = "portfolios", // Optional with default
  }, { rejectWithValue, dispatch }) => {
    try {
      const requestData = {
        portfolio_id,
        user_prompt,
        lookbacks
      };

      if (groupId) {
        requestData.group_id = groupId;
      }

      const response = await fetch(
        `${HOSTNAME}/inference/chat/${chatContext}`,
        prepareJsonPayload({
          data: requestData,
          method: "POST",
        })
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Network response was not ok: ${errorText}`);
      }

      // Log the user's message in the conversation
      dispatch(appendUserMessage({
        id,
        groupId,
        userChunk: user_prompt
      }));

      // Stream the response
      const reader = response.body.getReader();
      const decoder = new TextDecoder("utf-8");

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        dispatch(appendStreamResponse({ id, groupId, chunk }));
      }

      return { id, timestamp: new Date().toISOString() };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);


export const fetchMetricToTargetResponse = createAsyncThunk(
    'inference/fetchMetricToTargetResponse',
    /**
     *
     * @param id {string} - The UUID of the prompt generated in useGptResponses hook
     * @param metricName {string} - The prompt to send to server to passthrough to GPT service
     * @param currentValue {number} - The UUID of the prompt generated in useGptResponses hook
     * @param targetValue {number, optional} - Where within the platform the prompt was generated
     * @param rejectWithValue
     */
    async ({ id, metricName, currentValue, targetValue }, { rejectWithValue }) => {
        try {
            const response = await fetch(`${HOSTNAME}/inference/metrics?metricName=${metricName}&currentValue=${currentValue}&targetValue=${targetValue}`);

            if (!response.ok) {
                throw new Error('Network response was not ok');
            }
            const data = await response.json();
            return { id, data, timestamp: new Date().toISOString(), srcLocation: 'Metrics' };  // Include UUID in the response
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);



export const extractPortfoliosFromFile = createAsyncThunk(
  'inference/extractPortfoliosFromFile',
  async (file, { rejectWithValue }) => {
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(`${HOSTNAME}/inference/extract/portfolios`, {
        method: 'POST',
        body: formData,
        headers: {
          ...getAuthHeader(),
        },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to extract portfolios from file');
      }

      const responseData = await response.json();
      return { filename: file.name, data: responseData };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);


/**
 *
 */
export const deleteConversation = createAsyncThunk(
  'inference/deleteConversation',
  async ({ conversationId = null }, { rejectWithValue }) => {
    try {
      const response = await fetch(
        `${HOSTNAME}/inference/chat/delete/${conversationId}`,
        prepareJsonPayload({}, {method: "DELETE"})
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to delete conversation: ${errorText}`);
      }

      return { conversationId };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);



export const allocatePortfolios = createAsyncThunk(
  'inference/allocatePortfolios',
  /**
   * Fetch portfolio allocation recommendations based on provided portfolios and user responses
   * @param {Object} params - Contains portfolio IDs and user responses
   * @param {string[]} params.portfolioIds - Array of portfolio IDs to analyze
   * @param {Object} params.responses - Object containing user responses/preferences
   * @param {function} rejectWithValue - A function to handle rejections
   */
  async ({ portfolioIds, responses }, { rejectWithValue }) => {
    try {
      const response = await fetch(
        `${HOSTNAME}/inference/functions/portfolios/allocate`,
        prepareJsonPayload({
          data: {
            portfolio_ids: portfolioIds,
            responses: responses
          },
          method: "POST",
          headers: {
            ...getAuthHeader()
          }
        })
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to allocate portfolios');
      }

      const data = await response.json();
      return {
        portfolioIds,
        responses,
        data,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);
